import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Search, Plus, User, Mail, Check } from 'lucide-react';
import API_BASE_URL from '../config/api';

const RADIOLOGY_PRACTICES = [
  { name: 'River Radiology', searchKey: 'River Radiology' },
  { name: 'Pacific Radiology', searchKey: 'Pacific Radiology' },
  { name: 'I-Med Radiology', searchKey: 'I-Med Radiology' },
  { name: 'Bay Radiology', searchKey: 'Bay Radiology' },
  { name: 'Canopy Radiology', searchKey: 'Canopy Radiology' }
];

function RecipientSelector({ letter, patient, onConfirm, onCancel }) {
  const [recipients, setRecipients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [customName, setCustomName] = useState('');
  const [customType, setCustomType] = useState('other');
  const [selectedRadPractice, setSelectedRadPractice] = useState('');
  const [radPracticeEmail, setRadPracticeEmail] = useState('');
  const [radLookingUp, setRadLookingUp] = useState(false);
  const [radCustomName, setRadCustomName] = useState('');

  const isMRI = letter?.letterType === 'mri_referral';

  // Look up radiology practice email from clinics DB when selected
  useEffect(() => {
    if (!selectedRadPractice || selectedRadPractice === 'other') {
      setRadPracticeEmail('');
      return;
    }
    const practice = RADIOLOGY_PRACTICES.find(p => p.name === selectedRadPractice);
    if (!practice) return;

    let cancelled = false;
    setRadLookingUp(true);
    axios.get(`${API_BASE_URL}/clinics`, { params: { search: practice.searchKey } })
      .then(res => {
        if (cancelled) return;
        const clinics = res.data || [];
        const match = clinics.find(c => c.email);
        if (match) {
          setRadPracticeEmail(match.email);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRadLookingUp(false); });

    return () => { cancelled = true; };
  }, [selectedRadPractice]);

  // Auto-populate recipients from referral data, then look up missing emails
  useEffect(() => {
    const buildRecipients = async () => {
      const autoRecipients = [];

      // GP from clinical data or referral
      const gpName = letter?.form?.clinicalData?.gpName || letter?.referral?.gpName;
      const gpEmail = letter?.form?.clinicalData?.gpEmail;
      if (gpEmail) {
        autoRecipients.push({
          email: gpEmail,
          name: gpName || 'GP',
          recipientType: 'gp',
          source: 'auto'
        });
      }

      // Radiology referrals: skip referrer and patient (radiology practice added via dropdown)
      const isMRI = letter?.letterType === 'mri_referral';

      // Referrer from referral
      if (!isMRI) {
        const referrerName = letter?.referral?.referrerName;
        if (referrerName && !autoRecipients.some(r => r.recipientType === 'gp' && r.name === referrerName)) {
          autoRecipients.push({
            email: '',
            name: referrerName,
            recipientType: 'referrer',
            source: 'auto',
            needsEmail: true
          });
        }
      }

      // Patient email
      if (!isMRI && patient?.email) {
        autoRecipients.push({
          email: patient.email,
          name: patient.name,
          recipientType: 'patient',
          source: 'auto'
        });
      }

      // Auto-lookup emails for recipients that need them
      for (const r of autoRecipients) {
        if (!r.needsEmail) continue;
        try {
          // Try clinics directory first
          const clinicRes = await axios.get(`${API_BASE_URL}/clinics`, { params: { search: r.name } });
          const clinics = clinicRes.data || [];
          const match = clinics.find(c => c.email);
          if (match) {
            r.email = match.email;
            r.needsEmail = false;
            continue;
          }
          // Fallback: contacts suggest
          const contactRes = await axios.get(`${API_BASE_URL}/contacts/suggest`, { params: { q: r.name } });
          const contacts = contactRes.data || [];
          const contactMatch = contacts.find(c => c.email);
          if (contactMatch) {
            r.email = contactMatch.email;
            r.needsEmail = false;
          }
        } catch {
          // Keep needsEmail: true if lookup fails
        }
      }

      setRecipients(autoRecipients);
    };

    buildRecipients();
  }, [letter, patient]);

  // Search clinics directory
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/clinics`, {
          params: { search: searchQuery }
        });
        // Filter out clinics already in recipients
        const existingEmails = new Set(recipients.map(r => r.email).filter(Boolean));
        setSearchResults((res.data || []).filter(c => !existingEmails.has(c.email)));
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, recipients]);

  const addClinicAsRecipient = (clinic) => {
    setRecipients(prev => [...prev, {
      contactId: clinic.id,
      email: clinic.email || '',
      name: clinic.tradingName || clinic.name,
      recipientType: 'referrer',
      source: 'clinic',
      needsEmail: !clinic.email
    }]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const addCustomRecipient = () => {
    if (!customEmail) return;
    setRecipients(prev => [...prev, {
      email: customEmail,
      name: customName || customEmail,
      recipientType: customType,
      source: 'custom'
    }]);
    setCustomEmail('');
    setCustomName('');
    setCustomType('other');
    setAddingCustom(false);
  };

  const removeRecipient = (index) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const updateRecipientEmail = (index, email) => {
    setRecipients(prev => prev.map((r, i) =>
      i === index ? { ...r, email, needsEmail: !email } : r
    ));
  };

  const handleConfirm = () => {
    // Build final recipient list including radiology practice if selected
    let allRecipients = [...recipients];

    if (isMRI && radPracticeEmail && radPracticeEmail.includes('@')) {
      const practiceName = selectedRadPractice === 'other' ? (radCustomName || 'Radiology') : selectedRadPractice;
      // Avoid duplicates
      if (!allRecipients.some(r => r.email === radPracticeEmail)) {
        allRecipients.unshift({
          email: radPracticeEmail,
          name: practiceName,
          recipientType: 'radiology',
          source: 'radiology_practice'
        });
      }
    }

    // Filter out recipients without valid emails
    const validRecipients = allRecipients.filter(r => r.email && r.email.includes('@'));
    if (validRecipients.length === 0) {
      alert('Please add at least one recipient with a valid email address.');
      return;
    }

    if (isMRI && !radPracticeEmail) {
      if (!window.confirm('No radiology practice selected. Send without a radiology recipient?')) {
        return;
      }
    }

    onConfirm(validRecipients);
  };

  const typeColors = {
    gp: { bg: '#dcfce7', text: '#166534' },
    referrer: { bg: '#dbeafe', text: '#1e40af' },
    patient: { bg: '#fef3c7', text: '#92400e' },
    acc: { bg: '#fce7f3', text: '#9d174d' },
    radiology: { bg: '#e0e7ff', text: '#3730a3' },
    physio: { bg: '#d1fae5', text: '#065f46' },
    other: { bg: '#f3f4f6', text: '#374151' }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.5)'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '560px',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Select Recipients</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
              Choose who should receive this letter
            </p>
          </div>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b7280' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Recipients list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {/* Radiology Practice Selector (MRI referrals only) */}
          {isMRI && (
            <div style={{
              marginBottom: '16px',
              padding: '14px',
              backgroundColor: '#eef2ff',
              borderRadius: '8px',
              border: '1px solid #c7d2fe'
            }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#4338ca', marginBottom: '8px', textTransform: 'uppercase' }}>
                Radiology Practice
              </p>
              <select
                value={selectedRadPractice}
                onChange={(e) => {
                  setSelectedRadPractice(e.target.value);
                  setRadCustomName('');
                  setRadPracticeEmail('');
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #c7d2fe',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  marginBottom: '8px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">-- Select radiology practice --</option>
                {RADIOLOGY_PRACTICES.map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
                <option value="other">Other (free text)</option>
              </select>

              {selectedRadPractice === 'other' && (
                <input
                  type="text"
                  value={radCustomName}
                  onChange={(e) => setRadCustomName(e.target.value)}
                  placeholder="Practice name..."
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #c7d2fe',
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginBottom: '8px',
                    boxSizing: 'border-box'
                  }}
                />
              )}

              {(selectedRadPractice && selectedRadPractice !== '') && (
                <div>
                  {radLookingUp ? (
                    <p style={{ fontSize: '12px', color: '#6366f1', margin: 0 }}>Looking up email...</p>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Mail size={14} style={{ color: '#6366f1', flexShrink: 0 }} />
                      <input
                        type="email"
                        value={radPracticeEmail}
                        onChange={(e) => setRadPracticeEmail(e.target.value)}
                        placeholder="Email address for referral..."
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #c7d2fe',
                          borderRadius: '6px',
                          fontSize: '13px',
                          backgroundColor: radPracticeEmail ? '#f0fdf4' : 'white'
                        }}
                      />
                      {radPracticeEmail && radPracticeEmail.includes('@') && (
                        <Check size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {recipients.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>
                Recipients ({recipients.length})
              </p>
              {recipients.map((r, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '6px'
                }}>
                  <User size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>{r.name}</span>
                      <span style={{
                        fontSize: '10px',
                        padding: '1px 6px',
                        borderRadius: '4px',
                        backgroundColor: (typeColors[r.recipientType] || typeColors.other).bg,
                        color: (typeColors[r.recipientType] || typeColors.other).text,
                        fontWeight: '500'
                      }}>
                        {r.recipientType}
                      </span>
                    </div>
                    {r.needsEmail ? (
                      <input
                        type="email"
                        value={r.email}
                        onChange={(e) => updateRecipientEmail(i, e.target.value)}
                        placeholder="Enter email address..."
                        style={{
                          marginTop: '4px',
                          width: '100%',
                          padding: '4px 8px',
                          border: '1px solid #fca5a5',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: '#fff7ed'
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        <Mail size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                        {r.email}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeRecipient(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Search contacts */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clinics..."
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 34px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {searchResults.length > 0 && (
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                marginTop: '4px',
                maxHeight: '160px',
                overflowY: 'auto',
                backgroundColor: 'white'
              }}>
                {searchResults.map(clinic => (
                  <button
                    key={clinic.id}
                    onClick={() => addClinicAsRecipient(clinic)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      border: 'none',
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <Plus size={14} style={{ color: '#2563eb' }} />
                    <div>
                      <div style={{ fontWeight: '500' }}>{clinic.tradingName || clinic.name}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        {clinic.city ? `${clinic.city} - ` : ''}{clinic.email || 'No email'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searching && (
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Searching...</p>
            )}
          </div>

          {/* Add custom recipient */}
          {!addingCustom ? (
            <button
              onClick={() => setAddingCustom(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                backgroundColor: 'transparent',
                border: '1px dashed #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#6b7280',
                width: '100%'
              }}
            >
              <Plus size={14} /> Add custom recipient
            </button>
          ) : (
            <div style={{
              padding: '12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: '#f9fafb'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Name"
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                />
                <select
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                >
                  <option value="gp">GP</option>
                  <option value="referrer">Referrer</option>
                  <option value="patient">Patient</option>
                  <option value="acc">ACC</option>
                  <option value="radiology">Radiology</option>
                  <option value="physio">Physio</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <input
                type="email"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                placeholder="Email address"
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={addCustomRecipient}
                  disabled={!customEmail}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: customEmail ? '#2563eb' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: customEmail ? 'pointer' : 'not-allowed',
                    fontSize: '13px'
                  }}
                >
                  Add
                </button>
                <button
                  onClick={() => setAddingCustom(false)}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: '#e5e7eb',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Check size={16} />
            Confirm & Mark Ready to Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default RecipientSelector;
